import React from 'react';
import { useFormContext } from 'react-hook-form';
import { ClientAddress } from '@/app/(main)/client-detail/edit-main/components/ClientAdress';
import type { CreateClientFormData } from '@/types/clientsApiTypes';
import { MAILING_FIELDS, MAILING_TO_BILLING_MAP } from '../../const/passenger';
import { getAddressPayload } from '../../utils/getAddressPayload';

type MailingField = keyof typeof MAILING_TO_BILLING_MAP;

interface MailingAddressSectionProps {
  onUpdateFormData?: (fieldName: string, fieldValue: unknown) => void;
}

export const MailingAddressSection = ({ onUpdateFormData }: MailingAddressSectionProps) => {
  const { watch, setValue, getValues } = useFormContext<CreateClientFormData>();
  const relatedPassengers = watch('related_passengers') || [];
  const isBillingSameAsMailing = watch('primary_passenger.client.is_billing_same_as_mailing');

  const syncAddressToBilling = (mailingData: { [key: string]: string }) => {
    if (!isBillingSameAsMailing) return;

    const billingPayload: Record<string, string> = {};
    
    (Object.keys(mailingData) as MailingField[]).forEach((mailingField: MailingField) => {
      const billingField = MAILING_TO_BILLING_MAP[mailingField];
      const value = mailingData[mailingField] ?? '';
      billingPayload[billingField] = value;
      setValue(`primary_passenger.client.${billingField}`, value);
    });

    onUpdateFormData?.('primary_passenger.client', billingPayload);
  };

  const syncAddressToRelatedPassengers = (addressData: { [key: string]: string }) => {
    relatedPassengers.forEach((passenger, index) => {
      if (passenger.client.same_as_primary) {
        MAILING_FIELDS.forEach((field) => {
          const fieldName = `related_passengers.${index}.client.${field}` as const;
          const value = addressData[field] ?? '';
          setValue(fieldName, value);
        });
        onUpdateFormData?.(`related_passengers.${index}.client`, addressData);
      }
    });
  };

  const handleSaveOnBlur = (name: string, value: unknown) => {
    onUpdateFormData?.(name, value);

    const fieldName = name.split('.').pop();
    if (fieldName && MAILING_FIELDS.includes(fieldName as typeof MAILING_FIELDS[number])) {
      const primaryPassenger = getValues('primary_passenger.client');
      const addressData: { [key: string]: string } = {};
      MAILING_FIELDS.forEach((field) => {
        addressData[field] = primaryPassenger[field] ?? '';
      });
      syncAddressToRelatedPassengers(addressData);
      syncAddressToBilling(addressData);
    }
  };

  const selectPlaceAddress = (address: { [key: string]: string }) => {
    const addressPayload = getAddressPayload(address);
    onUpdateFormData?.('primary_passenger.client', { ...addressPayload });

    syncAddressToRelatedPassengers(addressPayload);
    syncAddressToBilling(addressPayload);
  };

  return (
    <ClientAddress
      selectPlaceAddress={selectPlaceAddress}
      onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleSaveOnBlur(e.target.name, e.target.value)}
      fields={{
        address_line1: {
          label: 'Address Line 1',
          placeholder: 'Street address, P.O. box',
          name: 'primary_passenger.client.client_mailing_address',
        },
        address_line2: {
          label: 'Address Line 2',
          placeholder: 'Apartment, suite, unit, building',
          name: 'primary_passenger.client.client_mailing_address_line_2',
        },
        city: {
          label: 'City',
          placeholder: 'Enter city',
          name: 'primary_passenger.client.client_mailing_city',
        },
        state: {
          label: 'State/Province',
          placeholder: 'Select state or province',
          name: 'primary_passenger.client.client_mailing_state',
        },
        zip: {
          label: 'Zip Code',
          placeholder: 'Enter ZIP or postal code',
          name: 'primary_passenger.client.zip_code',
        },
        country: {
          label: 'Country',
          placeholder: 'Select country',
          name: 'primary_passenger.client.client_mailing_country',
        },
      }}
    />
  );
};
